import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, useMapEvents } from 'react-leaflet'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import SitrepsTab from './SitrepsTab'
import ResourcesTab from './ResourcesTab'
import {
    emergencyService, KIND_LABEL, PHASE_LABEL, ROLE_LABEL, LOG_LABEL, SYSTEM_KINDS,
    type Emergency, type EmergencyCommand, type EmergencyKind, type EmergencyPhase, type CommandRole,
    type LogEntry, type LogKind, type KindTotal,
} from '@/services/emergencyService'

const inputCls = 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-blue-500/50 focus:outline-none'
const btnPrimary = 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50'
const btnGhost = 'rounded-xl border border-white/10 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5'

const PHASE_COLOR: Record<EmergencyPhase, string> = {
    ALERT: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    RESPONSE: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
    RECOVERY: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
    CLOSED: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
}
const NEXT_PHASE: Partial<Record<EmergencyPhase, EmergencyPhase>> = { ALERT: 'RESPONSE', RESPONSE: 'RECOVERY' }

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const errorMessage = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

/** Lista y detalle de emergencias (nivel, fases, mando, bitácora e informe de cierre). */
export default function EmergenciesPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const canCommand = !!user?.roles?.some((r) => ['ADMINISTRADOR', 'COORDINADOR', 'SUPERVISOR'].includes(r))
    const canApprove = !!user?.roles?.some((r) => ['ADMINISTRADOR', 'COORDINADOR'].includes(r))
    const [list, setList] = useState<Emergency[]>([])
    const [includeClosed, setIncludeClosed] = useState(false)
    const [creating, setCreating] = useState(false)

    const load = useCallback(() => {
        emergencyService.list(includeClosed).then(setList).catch(() => toast.error('No se pudieron cargar las emergencias'))
    }, [includeClosed])
    useEffect(() => { load() }, [load])

    if (id) {
        return <EmergencyDetail id={Number(id)} canCommand={canCommand} canApprove={canApprove} onBack={() => { navigate('/emergencies'); load() }} />
    }

    return (
        <div>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-white">Emergencias</h1>
                    <p className="text-sm text-gray-500">Eventos mayores: nivel, fases, cadena de mando, bitácora y cierre.</p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-gray-400">
                        <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} /> Ver cerradas
                    </label>
                    {canCommand && <button onClick={() => setCreating(true)} className={btnPrimary}>Registrar emergencia</button>}
                </div>
            </div>

            {list.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-8 text-center text-sm text-gray-500">No hay emergencias {includeClosed ? 'registradas' : 'abiertas'}.</div>
            ) : (
                <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {list.map((e) => (
                        <li key={e.id}>
                            <button onClick={() => navigate(`/emergencies/${e.id}`)}
                                className="w-full rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-left hover:bg-white/[0.05]">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-gray-500">{e.code}</span>
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${PHASE_COLOR[e.phase]}`}>{PHASE_LABEL[e.phase]}</span>
                                    <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-gray-300">Nivel {e.level}</span>
                                </div>
                                <div className="mt-1 font-semibold text-white">{e.title}</div>
                                <div className="text-xs text-gray-500">{KIND_LABEL[e.kind]} · desde {fmt(e.startedAt)}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {creating && <CreateDialog onClose={() => setCreating(false)} onCreated={(e) => { setCreating(false); navigate(`/emergencies/${e.id}`) }} />}
        </div>
    )
}

/** Mapa para dibujar el área: cada clic agrega un vértice. */
function AreaPicker({ ring, onChange }: { ring: number[][]; onChange: (r: number[][]) => void }) {
    function Clicks() {
        useMapEvents({ click: (ev) => onChange([...ring, [ev.latlng.lng, ev.latlng.lat]]) })
        return null
    }
    return (
        <div>
            <div className="h-64 overflow-hidden rounded-xl border border-white/10">
                <MapContainer center={[-33.45, -70.65]} zoom={10} className="h-full w-full">
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    <Clicks />
                    {ring.length >= 3 && <Polygon positions={ring.map(([lon, lat]) => [lat, lon] as [number, number])} pathOptions={{ color: '#f59e0b' }} />}
                    {ring.map(([lon, lat], i) => <CircleMarker key={i} center={[lat, lon]} radius={4} pathOptions={{ color: '#f59e0b' }} />)}
                </MapContainer>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                <span>Haz clic en el mapa para marcar el área ({ring.length} puntos{ring.length > 0 && ring.length < 3 ? ', mínimo 3' : ''}).</span>
                {ring.length > 0 && <button type="button" onClick={() => onChange([])} className="text-gray-400 hover:text-white">Borrar área</button>}
            </div>
        </div>
    )
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (e: Emergency) => void }) {
    const [form, setForm] = useState({ title: '', kind: 'WILDFIRE' as EmergencyKind, level: 1, triggerNote: '', description: '' })
    const [ring, setRing] = useState<number[][]>([])
    const [saving, setSaving] = useState(false)
    const submit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        setSaving(true)
        try {
            onCreated(await emergencyService.create({ ...form, area: ring.length >= 3 ? ring : undefined }))
            toast.success('Emergencia registrada')
        } catch (e) {
            toast.error(errorMessage(e, 'No se pudo registrar'))
        } finally {
            setSaving(false)
        }
    }
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
            <form role="dialog" aria-label="Registrar emergencia" onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="my-8 w-full max-w-2xl space-y-3 rounded-2xl border border-white/10 bg-[#0b0d12] p-6">
                <h2 className="text-lg font-semibold text-white">Registrar emergencia</h2>
                <input aria-label="Título" placeholder="Título (p. ej. Incendio Quebrada Alta)" required maxLength={160} value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                    <select aria-label="Tipo" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as EmergencyKind })} className={inputCls}>
                        {(Object.keys(KIND_LABEL) as EmergencyKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                    </select>
                    <select aria-label="Nivel" value={form.level} onChange={(e) => setForm({ ...form, level: Number(e.target.value) })} className={inputCls}>
                        <option value={1}>Nivel 1 — local</option>
                        <option value={2}>Nivel 2 — regional</option>
                        <option value={3}>Nivel 3 — nacional</option>
                    </select>
                </div>
                <input aria-label="Alerta que la activó" placeholder="Alerta que la activó (p. ej. alerta roja regional)" maxLength={300} value={form.triggerNote}
                    onChange={(e) => setForm({ ...form, triggerNote: e.target.value })} className={inputCls} />
                <textarea aria-label="Descripción" placeholder="Descripción" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
                <AreaPicker ring={ring} onChange={setRing} />
                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onClose} className={btnGhost}>Cancelar</button>
                    <button type="submit" disabled={saving} className={btnPrimary}>Registrar</button>
                </div>
            </form>
        </div>
    )
}

type Tab = 'summary' | 'command' | 'log' | 'resources' | 'sitreps' | 'closure'

function EmergencyDetail({ id, canCommand, canApprove, onBack }: { id: number; canCommand: boolean; canApprove: boolean; onBack: () => void }) {
    const [e, setE] = useState<Emergency | null>(null)
    const [tab, setTab] = useState<Tab>('summary')
    const [command, setCommand] = useState<EmergencyCommand[]>([])
    const [log, setLog] = useState<LogEntry[]>([])
    const [totals, setTotals] = useState<KindTotal[]>([])

    const reload = useCallback(async () => {
        try {
            const [em, cm, lg, tt] = await Promise.all([
                emergencyService.get(id), emergencyService.command(id), emergencyService.log(id), emergencyService.totals(id),
            ])
            setE(em); setCommand(cm); setLog(lg); setTotals(tt)
        } catch {
            toast.error('No se pudo cargar la emergencia')
        }
    }, [id])
    useEffect(() => { reload() }, [reload])

    const act = async (fn: () => Promise<unknown>, ok: string) => {
        try { await fn(); toast.success(ok); reload() } catch (err) { toast.error(errorMessage(err, 'No se pudo aplicar el cambio')) }
    }

    if (!e) return <div className="text-gray-500">Cargando…</div>
    const open = e.phase !== 'CLOSED'
    const next = NEXT_PHASE[e.phase]
    const active = command.filter((c) => c.active)

    return (
        <div>
            <button onClick={onBack} className="mb-3 text-xs text-gray-500 hover:text-white">← Emergencias</button>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500">{e.code}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${PHASE_COLOR[e.phase]}`}>{PHASE_LABEL[e.phase]}</span>
                        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-gray-300">Nivel {e.level}</span>
                    </div>
                    <h1 className="mt-1 text-2xl font-bold text-white">{e.title}</h1>
                    <div className="text-xs text-gray-500">{KIND_LABEL[e.kind]} · desde {fmt(e.startedAt)}{e.closedAt ? ` · cerrada ${fmt(e.closedAt)}` : ''}</div>
                </div>
                {canCommand && (
                    <div className="flex flex-wrap gap-2">
                        {open && next && (
                            <button onClick={() => act(() => emergencyService.setPhase(id, next), `Fase: ${PHASE_LABEL[next]}`)} className={btnGhost}>
                                Pasar a {PHASE_LABEL[next]}
                            </button>
                        )}
                        {open && (
                            <select aria-label="Cambiar nivel" value={e.level}
                                onChange={(ev) => act(() => emergencyService.setLevel(id, Number(ev.target.value)), 'Nivel actualizado')}
                                className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-gray-300">
                                {[1, 2, 3].map((n) => <option key={n} value={n}>Nivel {n}</option>)}
                            </select>
                        )}
                        {!open && <button onClick={() => act(() => emergencyService.reopen(id), 'Emergencia reabierta')} className={btnGhost}>Reabrir</button>}
                    </div>
                )}
            </div>

            <div className="mb-4 flex gap-1 border-b border-white/10" role="tablist">
                {([['summary', 'Resumen'], ['command', `Mando (${active.length})`], ['log', `Bitácora (${log.length})`], ['resources', 'Recursos'], ['sitreps', 'Reportes'], ['closure', 'Cierre']] as [Tab, string][]).map(([t, label]) => (
                    <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === t ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'summary' && <SummaryTab e={e} totals={totals} active={active} />}
            {tab === 'command' && <CommandTab id={id} items={command} canCommand={canCommand && open} onChanged={reload} />}
            {tab === 'log' && <LogTab id={id} items={log} open={open} canCommand={canCommand} onChanged={reload} />}
            {tab === 'resources' && <ResourcesTab emergencyId={id} open={open} canCommand={canCommand} />}
            {tab === 'sitreps' && <SitrepsTab emergencyId={id} open={open} canCommand={canCommand} canApprove={canApprove} />}
            {tab === 'closure' && <ClosureTab id={id} e={e} canCommand={canCommand} onChanged={reload} />}
        </div>
    )
}

function SummaryTab({ e, totals, active }: { e: Emergency; totals: KindTotal[]; active: EmergencyCommand[] }) {
    const positions = useMemo(() => (e.areaRing ?? []).map(([lon, lat]) => [lat, lon] as [number, number]), [e.areaRing])
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
                {e.description && <p className="text-sm text-gray-300">{e.description}</p>}
                {e.triggerNote && <p className="text-xs text-gray-500">Activada por: {e.triggerNote}</p>}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {totals.length === 0 ? <div className="text-xs text-gray-500">Sin novedades cuantificadas todavía.</div> : totals.map((t) => (
                        <div key={t.kind} className="rounded-xl border border-white/10 p-3">
                            <div className="text-2xl font-bold text-white">{t.quantity}</div>
                            <div className="text-xs text-gray-500">{LOG_LABEL[t.kind]} · {t.entries} registro{t.entries === 1 ? '' : 's'}</div>
                        </div>
                    ))}
                </div>
                {positions.length >= 3 && (
                    <div className="h-72 overflow-hidden rounded-xl border border-white/10">
                        <MapContainer bounds={positions} className="h-full w-full">
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                            <Polygon positions={positions} pathOptions={{ color: '#f59e0b' }}><Tooltip>{e.code}</Tooltip></Polygon>
                        </MapContainer>
                    </div>
                )}
            </div>
            <div className="rounded-xl border border-white/10 p-4">
                <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">Mando vigente</h3>
                {active.length === 0 ? <div className="text-xs text-gray-500">Sin cargos designados.</div> : (
                    <ul className="space-y-2 text-sm">
                        {active.map((c) => (
                            <li key={c.id}>
                                <div className="text-gray-500">{ROLE_LABEL[c.role]}</div>
                                <div className="text-white">{c.personName}{c.organization ? ` · ${c.organization}` : ''}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}

function CommandTab({ id, items, canCommand, onChanged }: { id: number; items: EmergencyCommand[]; canCommand: boolean; onChanged: () => void }) {
    const [form, setForm] = useState({ role: 'COMMANDER' as CommandRole, personName: '', organization: '', phone: '', note: '' })
    const submit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        try {
            await emergencyService.assignCommand(id, form)
            setForm({ ...form, personName: '', organization: '', phone: '', note: '' })
            toast.success('Cargo designado')
            onChanged()
        } catch (e) { toast.error(errorMessage(e, 'No se pudo designar')) }
    }
    return (
        <div className="space-y-4">
            {canCommand && (
                <form onSubmit={submit} className="grid grid-cols-1 gap-2 rounded-xl border border-white/10 p-4 sm:grid-cols-5">
                    <select aria-label="Cargo" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as CommandRole })} className={inputCls}>
                        {(Object.keys(ROLE_LABEL) as CommandRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                    <input aria-label="Nombre" placeholder="Nombre" required value={form.personName} onChange={(e) => setForm({ ...form, personName: e.target.value })} className={inputCls} />
                    <input aria-label="Organización" placeholder="Organización" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className={inputCls} />
                    <input aria-label="Teléfono" placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                    <button type="submit" className={btnPrimary}>Designar</button>
                    <p className="text-xs text-gray-500 sm:col-span-5">Si el cargo ya tiene titular, se registra como traspaso y el anterior queda cerrado.</p>
                </form>
            )}
            <ul className="space-y-2">
                {items.length === 0 && <li className="text-xs text-gray-500">Sin cargos registrados.</li>}
                {[...items].reverse().map((c) => (
                    <li key={c.id} className={`flex flex-wrap items-center gap-3 rounded-xl border border-white/10 p-3 text-sm ${c.active ? '' : 'opacity-50'}`}>
                        <span className="w-48 text-gray-400">{ROLE_LABEL[c.role]}</span>
                        <span className="font-medium text-white">{c.personName}</span>
                        <span className="text-xs text-gray-500">{c.organization}{c.phone ? ` · ${c.phone}` : ''}</span>
                        <span className="ml-auto text-xs text-gray-500">{fmt(c.assignedAt)}{c.endedAt ? ` → ${fmt(c.endedAt)}` : ' · vigente'}</span>
                        {canCommand && c.active && (
                            <button onClick={() => emergencyService.endCommand(id, c.id).then(onChanged)} className={btnGhost}>Liberar</button>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}

function LogTab({ id, items, open, canCommand, onChanged }: { id: number; items: LogEntry[]; open: boolean; canCommand: boolean; onChanged: () => void }) {
    const [form, setForm] = useState({ kind: 'NOTE' as LogKind, quantity: '', description: '', sector: '' })
    const submit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        try {
            await emergencyService.addLog(id, { ...form, quantity: form.quantity === '' ? undefined : Number(form.quantity) })
            setForm({ ...form, quantity: '', description: '', sector: '' })
            toast.success('Novedad registrada')
            onChanged()
        } catch (e) { toast.error(errorMessage(e, 'No se pudo registrar la novedad')) }
    }
    return (
        <div className="space-y-4">
            {open && (
                <form onSubmit={submit} className="grid grid-cols-1 gap-2 rounded-xl border border-white/10 p-4 sm:grid-cols-6">
                    <select aria-label="Tipo de novedad" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as LogKind })} className={inputCls}>
                        {(Object.keys(LOG_LABEL) as LogKind[]).filter((k) => !SYSTEM_KINDS.includes(k)).map((k) => <option key={k} value={k}>{LOG_LABEL[k]}</option>)}
                    </select>
                    <input aria-label="Cantidad" type="number" min={0} placeholder="Cantidad" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={inputCls} />
                    <input aria-label="Sector" placeholder="Sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className={inputCls} />
                    <input aria-label="Descripción de la novedad" placeholder="Descripción" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                    <button type="submit" className={btnPrimary}>Registrar</button>
                </form>
            )}
            <ul className="space-y-2">
                {items.length === 0 && <li className="text-xs text-gray-500">Sin novedades.</li>}
                {items.map((l) => {
                    const system = SYSTEM_KINDS.includes(l.kind)
                    return (
                        <li key={l.id} className={`flex flex-wrap items-start gap-3 rounded-xl border border-white/10 p-3 text-sm ${system ? 'bg-white/[0.02]' : ''}`}>
                            <span className="w-40 shrink-0 text-xs text-gray-400">{fmt(l.occurredAt)}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${system ? 'bg-gray-500/15 text-gray-400' : 'bg-blue-500/15 text-blue-300'}`}>{LOG_LABEL[l.kind]}</span>
                            {l.quantity !== null && <span className="font-semibold text-white">{l.quantity}</span>}
                            <span className="flex-1 text-gray-200">{l.description}{l.sector ? <span className="text-gray-500"> · {l.sector}</span> : null}</span>
                            <span className="text-xs text-gray-500">{l.reportedName ?? ''}</span>
                            {canCommand && open && !system && (
                                <button onClick={() => emergencyService.deleteLog(id, l.id).then(onChanged)} className="text-xs text-rose-300 hover:underline">Borrar</button>
                            )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

function ClosureTab({ id, e, canCommand, onChanged }: { id: number; e: Emergency; canCommand: boolean; onChanged: () => void }) {
    const [form, setForm] = useState({ results: e.reportResults ?? '', gaps: e.reportGaps ?? '', lessons: e.reportLessons ?? '', welfare: e.reportWelfare ?? '' })
    const closed = e.phase === 'CLOSED'
    const submit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        if (!window.confirm('¿Cerrar la emergencia con este informe? Los cargos vigentes quedarán liberados.')) return
        try {
            await emergencyService.close(id, form)
            toast.success('Emergencia cerrada')
            onChanged()
        } catch (err) { toast.error(errorMessage(err, 'No se pudo cerrar')) }
    }
    const field = (key: keyof typeof form, label: string, placeholder: string) => (
        <label className="block text-xs text-gray-400">{label}
            <textarea aria-label={label} rows={3} readOnly={closed} value={form[key]} placeholder={placeholder}
                onChange={(ev) => setForm({ ...form, [key]: ev.target.value })} className={`${inputCls} mt-1`} />
        </label>
    )
    return (
        <form onSubmit={submit} className="max-w-3xl space-y-3">
            <p className="text-xs text-gray-500">{closed ? 'Informe de cierre.' : 'El informe de cierre resume el evento y cierra la emergencia. Después se puede reabrir.'}</p>
            {field('results', 'Resultados', 'Qué se logró, estado final del evento')}
            {field('gaps', 'Brechas', 'Qué faltó: recursos, coordinación, información')}
            {field('lessons', 'Lecciones aprendidas', 'Qué hacer distinto la próxima vez')}
            {field('welfare', 'Salud y descanso del personal', 'Control de fatiga, relevos, atención médica')}
            {canCommand && !closed && <button type="submit" className={btnPrimary}>Cerrar emergencia</button>}
        </form>
    )
}
